<script setup lang="ts">
/**
 * Beneficial Owner Notices (Section 60C, M32108) — 설문 → 선택 문서만 Stage 패키지.
 */
import { computed, ref, watch } from 'vue';
import type { Component } from 'vue';
import { BO_NOTICE_DOCS, BO_NOTICE_STAGES, type BoNoticeDocCode } from './boNoticeFields';
import {
  normalizeSurvey,
  resolveIncludedDocs,
  summarizeSurvey,
  type BoNoticeSurvey,
} from './boNoticeSurvey';
import BoNoticeSurveyPanel from './BoNoticeSurvey.vue';
import BoNotice60C1CorporateDoc from './BoNotice60C1CorporateDoc.vue';
import BoNotice60C1IndividualDoc from './BoNotice60C1IndividualDoc.vue';
import BoNotice60C2IdentifiedDoc from './BoNotice60C2IdentifiedDoc.vue';
import BoNotice60C3Doc from './BoNotice60C3Doc.vue';
import BoNotice60C5ChangeDoc from './BoNotice60C5ChangeDoc.vue';
import BoNotice60C5ConfirmDoc from './BoNotice60C5ConfirmDoc.vue';

const props = withDefaults(defineProps<{
  studio?: boolean;
  editMode?: boolean;
  creating?: boolean;
  survey?: BoNoticeSurvey;
}>(), {
  studio: false,
  editMode: false,
  creating: false,
});

const emit = defineEmits<{
  (e: 'create-task'): void;
  (e: 'update:survey', v: BoNoticeSurvey): void;
}>();

const DOC_COMPONENTS: Record<BoNoticeDocCode, Component> = {
  BO01: BoNotice60C1CorporateDoc,
  BO02: BoNotice60C1IndividualDoc,
  BO03: BoNotice60C2IdentifiedDoc,
  BO04: BoNotice60C3Doc,
  BO05: BoNotice60C5ChangeDoc,
  BO06: BoNotice60C5ConfirmDoc,
};

const localSurvey = ref<BoNoticeSurvey>(normalizeSurvey(props.survey));

watch(
  () => props.survey,
  (v) => {
    localSurvey.value = normalizeSurvey(v);
    if (v?.completed) localSurvey.value.completed = true;
  },
  { deep: true },
);

function onSurveyUpdate(v: BoNoticeSurvey) {
  localSurvey.value = normalizeSurvey(v);
  localSurvey.value.completed = !!v.completed;
  emit('update:survey', { ...localSurvey.value });
}

const includedCodes = computed(() => resolveIncludedDocs(localSurvey.value));
const showSurvey = computed(() => !localSurvey.value.completed);

const currentStage = ref(1);

const view = computed(() => {
  const included = new Set(includedCodes.value);
  let n = 0;
  return BO_NOTICE_STAGES
    .map((st) => {
      const docs = (Object.values(BO_NOTICE_DOCS))
        .filter((d) => d.stage === st.no && included.has(d.docCode))
        .map((d) => ({ ...d, comp: DOC_COMPONENTS[d.docCode], no: (n += 1) }));
      return { ...st, docs };
    })
    .filter((st) => st.docs.length > 0);
});

watch(view, (stages) => {
  if (!stages.length) {
    currentStage.value = 1;
    return;
  }
  if (!stages.some((s) => s.no === currentStage.value)) {
    currentStage.value = stages[0].no;
  }
}, { immediate: true });

const stageIndex = computed(() => view.value.findIndex((s) => s.no === currentStage.value));
const totalStages = computed(() => view.value.length);
const totalDocs = computed(() => view.value.reduce((s, st) => s + st.docs.length, 0));
const createLabel = computed(() => (props.editMode ? 'Update Task' : 'Create Task'));
const surveySummary = computed(() => summarizeSurvey(localSurvey.value));

function go(n: number) {
  if (view.value.some((s) => s.no === n)) currentStage.value = n;
}
function goPrev() {
  const i = stageIndex.value;
  if (i > 0) currentStage.value = view.value[i - 1].no;
}
function goNext() {
  const i = stageIndex.value;
  if (i >= 0 && i < view.value.length - 1) currentStage.value = view.value[i + 1].no;
}

function reopenSurvey() {
  const next = normalizeSurvey({ ...localSurvey.value, completed: false });
  next.completed = false;
  localSurvey.value = next;
  emit('update:survey', next);
}
</script>

<template>
  <div class="allot-pkg">
    <BoNoticeSurveyPanel
      v-if="showSurvey"
      :model-value="localSurvey"
      @update:model-value="onSurveyUpdate"
      @complete="onSurveyUpdate"
    />

    <template v-else>
      <div class="sv-bar">
        <div class="sv-bar-txt">
          <b>Selected notices</b>
          <small>{{ surveySummary }}</small>
        </div>
        <button type="button" class="sv-bar-btn" @click="reopenSurvey">Change selection</button>
      </div>

      <div v-if="view.length > 1" class="stepper">
        <button
          v-for="(st, idx) in view" :key="st.no"
          class="step" :class="{ active: currentStage === st.no, done: stageIndex > idx }"
          :title="`Stage ${st.no} · ${st.en}`" @click="go(st.no)"
        >
          <span class="step-no">{{ stageIndex > idx ? '✓' : (idx + 1) }}</span>
          <span class="step-txt"><b>Stage {{ st.no }}</b><small>{{ st.en }}</small></span>
        </button>
      </div>

      <section v-for="st in view" :key="st.no" v-show="currentStage === st.no" class="stage">
        <div class="stage-head">
          <span class="stage-badge">Stage {{ st.no }}</span>
          <span class="stage-t">{{ st.en }}</span>
        </div>

        <div v-for="d in st.docs" :key="d.docCode" class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">{{ d.icon }}</span>
            <span class="nm">{{ d.label }}</span>
            <span class="no">Doc {{ d.no }} / {{ totalDocs }}</span>
          </div>
          <div class="pkg-paper"><component :is="d.comp" /></div>
        </div>
      </section>

      <div class="stage-nav">
        <button v-if="stageIndex > 0" class="nav-btn" @click="goPrev">← Previous</button>
        <span class="nav-count">
          <template v-if="totalStages > 1">Stage {{ stageIndex + 1 }} / {{ totalStages }}</template>
          <template v-else>{{ totalDocs }} notice{{ totalDocs === 1 ? '' : 's' }}</template>
        </span>
        <button
          v-if="stageIndex >= 0 && stageIndex < totalStages - 1"
          class="nav-btn primary"
          @click="goNext"
        >Next →</button>
        <button
          v-else-if="!studio"
          class="nav-btn primary"
          :disabled="creating || !totalDocs"
          @click="emit('create-task')"
        >{{ creating ? (editMode ? 'Updating…' : 'Creating…') : createLabel }}</button>
      </div>
    </template>
  </div>
</template>

<style src="./boNoticeDoc.css"></style>

<style scoped>
.allot-pkg { color: #111; }
.sv-bar {
  display: flex; align-items: center; gap: 12px;
  width: 100%; max-width: 900px; margin: 0 auto 14px;
  padding: 10px 12px; background: #f4f7fc; border: 1px solid #e0e8f4; border-radius: 12px;
}
.sv-bar-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.sv-bar-txt b { font-size: 12px; color: #1f2d4d; }
.sv-bar-txt small { font-size: 11px; color: #6b778c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sv-bar-btn {
  flex: none; border: 1px solid #d3dceb; background: #fff; color: #42506a;
  font-size: 12px; font-weight: 800; border-radius: 8px; padding: 7px 11px; cursor: pointer;
}
.sv-bar-btn:hover { background: #eef3fb; }
.stepper { display: flex; gap: 8px; width: 100%; max-width: 900px; margin: 0 auto 18px; padding: 10px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20, 30, 60, .06); }
.step { flex: 1; display: flex; align-items: center; gap: 9px; padding: 9px 11px; border: 1px solid transparent; border-radius: 10px; background: #f6f8fc; cursor: pointer; text-align: left; transition: all .15s; }
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 12px; display: inline-flex; align-items: center; justify-content: center; }
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.step-txt b { font-size: 12px; color: #1f2d4d; }
.step-txt small { font-size: 10.5px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stage { width: 100%; max-width: 900px; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 10px; margin: 4px 0 14px; padding: 9px 4px; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 4px 11px; border-radius: 999px; letter-spacing: .02em; }
.stage-t { font-weight: 800; font-size: 14px; color: #1f2d4d; }
.pkg-doc { width: 100%; margin: 0 auto; }
.pkg-doc + .pkg-doc { margin-top: 30px; }
.pkg-doclabel { display: flex; align-items: center; gap: 7px; padding: 0 4px 9px; }
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: .01em; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.pkg-paper { background: #fff; padding: 34px 38px; box-shadow: 0 4px 24px rgba(20, 30, 60, .12); font-family: Arial, 'Malgun Gothic', sans-serif; color: #111; font-size: 12px; line-height: 1.4; border-radius: 2px; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 14px; width: 100%; max-width: 900px; margin: 26px auto 6px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 10px 18px; cursor: pointer; }
.nav-btn:hover:not(:disabled) { background: #f2f6fc; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.nav-btn.primary:hover:not(:disabled) { background: #1550b8; }
.nav-btn:disabled { opacity: .55; cursor: default; }
.nav-count { font-size: 12px; font-weight: 800; color: #8a97b5; }
</style>
